import {nanoid, createSlice, PayloadAction} from '@reduxjs/toolkit';
import api from '../api';

export interface Folder {
  name: string;
  id: string;
  date: string;
  default?: boolean;
}

export const defaultFolderTitle = 'folder.default-folder';

const generateDefaultFolder = () => {
  return {
    id: nanoid(),
    name: defaultFolderTitle,
    date: new Date().toISOString(),
    default: true
  } as Folder;
};

export const foldersSlice = createSlice({
  name: 'folders',
  initialState: [
    generateDefaultFolder()
  ],
  reducers: {
    createFolder: (state, action: PayloadAction<string>) => {
      const name = action.payload;

      const folder = {
        id: nanoid(),
        name,
        date: new Date().toISOString(),
        default: false
      } as Folder;

      state.push(folder);
    },
    deleteFolder: (state, action) => {
      return state.filter(folder => {
        // Not possible to delete default folders
        if (folder.default) return true;

        // Filter by folder id
        return folder.id !== action.payload;
      });
    },
    renameFolder: (state, action) => {
      const folder = state.find(folder => folder.id === action.payload.id);
      if (!folder) return;

      folder.name = action.payload.name;
      if (!folder.default) return;

      // Add new default folder
      const newDefaultFolder = generateDefaultFolder();
      state.push(newDefaultFolder);

      // Set the current folder to custom
      folder.default = false;
    }
  }
});

/** Create a new folder and return the new folderID */
export const createFolder = (name = defaultFolderTitle) => {
  return async (dispatch, getState) => {
    // Create new folder
    dispatch(foldersSlice.actions.createFolder(name));

    const folders = getState().folders as Folder[];
    const [folder] = folders.slice(-1);

    // Update cloud site
    await api.folders.create(folder);

    return folder.id;
  };
};

export const {
  deleteFolder,
  renameFolder
} = foldersSlice.actions;

export default foldersSlice.reducer;

export const selectDefaultFolder = state =>
  state.folders.find(folder => folder.default);

export const selectAllFolders = state => state.folders;
